/**
 * Turns off the browser's per-field input history ("previously typed values" popup) app-wide.
 *
 * That popup is a desktop-browser feature with no place in this app: it lists stale searches and
 * half-typed doses under every field, and on phones it covers the very results list the user is
 * trying to read. The App* form wrappers already default `autocomplete="off"`; this catches the
 * ~200 raw `VTextField` / `VAutocomplete` / `VSelect` / `VCombobox` fields that bypass them, and any
 * plain `<input>`.
 *
 * Rules — deliberately conservative:
 *   - an input that ALREADY has an `autocomplete` attribute is never touched, so the login form's
 *     `username` / `current-password` (and any field that opts in on purpose) keep the browser's
 *     saved-credential autofill;
 *   - password, hidden and non-text controls are skipped;
 *   - anything inside `[data-allow-autofill]` is skipped — the escape hatch for a future form that
 *     genuinely wants history (e.g. an address form).
 *
 * One `MutationObserver` on `<body>` for the app's lifetime; it only looks at added subtrees, so
 * the cost is proportional to what Vue renders, not to the page size.
 */

const SKIPPED_TYPES = new Set(["password", "hidden", "checkbox", "radio", "file", "range", "color", "submit", "button", "reset", "image"]);

function stamp(el: Element) {
  if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement))
    return;

  if (el.hasAttribute("autocomplete"))
    return;

  if (el instanceof HTMLInputElement && SKIPPED_TYPES.has(el.type))
    return;

  if (el.closest("[data-allow-autofill]"))
    return;

  el.setAttribute("autocomplete", "off");
}

function stampTree(root: Element) {
  stamp(root);
  root.querySelectorAll("input, textarea").forEach(stamp);
}

let installed = false;

export function installFormHistorySuppression() {
  if (installed || typeof document === "undefined" || typeof MutationObserver === "undefined")
    return;

  installed = true;

  const start = () => {
    stampTree(document.body);

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE)
            stampTree(node as Element);
        });
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.body)
    start();
  else
    document.addEventListener("DOMContentLoaded", start, { once: true });
}
